import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast } from "../../../utils/dialog";

export default function AdminLocalAttractions() {
  const { localAttractions, addLocalAttraction, updateLocalAttraction, deleteLocalAttraction } =
    useData();

  const [newAttraction, setNewAttraction] = useState({
    title: "",
    description: "",
    image: "",
    link: "",
  });

  const [file, setFile] = useState(null); // File for upload
  const [editingAttraction, setEditingAttraction] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAttraction((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]); // Set the selected file
  };

  const handleAddAttraction = async () => {
    if (newAttraction.title && newAttraction.description) {
      await addLocalAttraction(newAttraction, file); // Pass the file to the method
      setNewAttraction({ title: "", description: "", image: "", link: "" });
      setFile(null); // Reset the file input
    }
  };

  const handleUpdateAttraction = async () => {
    if (editingAttraction && newAttraction.title && newAttraction.description) {
      await updateLocalAttraction(editingAttraction.id, newAttraction, file); // Pass the file to the method
      setEditingAttraction(null);
      setNewAttraction({ title: "", description: "", image: "", link: "" });
      setFile(null); // Reset the file input
    }
  };

  const handleEdit = (attraction) => {
    setEditingAttraction(attraction);
    setNewAttraction({
      title: attraction.title || "",
      description: attraction.description || "",
      image: attraction.image || "",
      link: attraction.link || attraction.mapUrl || "",
    });
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete Attraction?",
        text: "This attraction will be permanently deleted.",
      })
    ) {
      await deleteLocalAttraction(id);
      successToast("Attraction deleted successfully.");
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Manage Local Attractions</h2>

      {/* Add/Edit Form */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {editingAttraction ? "Edit Attraction" : "Add New Attraction"}
        </h3>
        <input
          type="text"
          name="title"
          placeholder="Title"
          value={newAttraction.title}
          onChange={handleInputChange}
          className="block w-full mb-2 p-2 border rounded"
        />
        <textarea
          name="description"
          placeholder="Description"
          value={newAttraction.description}
          onChange={handleInputChange}
          className="block w-full mb-2 p-2 border rounded"
        />
        <input
          type="url"
          name="link"
          placeholder="Google Maps URL (e.g. https://maps.app.goo.gl/...)"
          value={newAttraction.link}
          onChange={handleInputChange}
          className="block w-full mb-2 p-2 border rounded"
        />
        <div className="mb-3">
          <ImageUploadField
            label="Image"
            hint="Recommended size: 1200×800px"
            previewUrl={newAttraction.image}
            file={file}
            onChange={handleFileChange}
            onClear={() => setFile(null)}
          />
        </div>
        <button
          onClick={editingAttraction ? handleUpdateAttraction : handleAddAttraction}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          {editingAttraction ? "Update Attraction" : "Add Attraction"}
        </button>
        {editingAttraction && (
          <button
            onClick={() => {
              setEditingAttraction(null);
              setNewAttraction({ title: "", description: "", image: "", link: "" });
              setFile(null); // Reset the file input
            }}
            className="ml-2 px-4 py-2 bg-gray-500 text-white rounded"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Attractions List */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Attractions ({localAttractions.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {localAttractions.map((attraction) => (
            <div key={attraction.id} className="p-4 border rounded shadow bg-white flex flex-col justify-between">
              <div>
                <img
                  src={attraction.image}
                  alt={attraction.title}
                  className="w-full h-36 object-cover mb-2 rounded"
                />
                <h4 className="text-lg font-bold text-gray-800">{attraction.title}</h4>
                <p className="text-sm text-gray-600 line-clamp-3 mb-2">{attraction.description}</p>
                {(attraction.link || attraction.mapUrl) && (
                  <a
                    href={attraction.link || attraction.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-2 font-medium"
                  >
                    <span>📍 View Location on Google Maps</span>
                    <span>↗</span>
                  </a>
                )}
              </div>
              <div className="mt-3 pt-2 border-t flex justify-between">
                <button
                  onClick={() => handleEdit(attraction)}
                  className="px-3 py-1 bg-yellow-500 text-white rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(attraction.id)}
                  className="px-3 py-1 bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}